// CC-052 — personal notification center (bell + panel). In-app only.
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, BellRing, Briefcase, HardHat, Ticket, UserRound, Wrench } from "lucide-react";
import api from "@/lib/api";

export const POLL_MS = 30000;
const ICONS = { candidate: UserRound, job: Wrench, ticket: Ticket, tech: HardHat, system: Briefcase };

export function fmtWhen(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-US", { timeZone: "America/Chicago", dateStyle: "medium", timeStyle: "short" });
}

function Row({ n, onOpen }) {
  const Icon = ICONS[n.type] || Bell;
  const unread = !n.read_at;
  return (
    <button type="button" onClick={() => onOpen(n)} data-testid={`notif-item-${n.id}`} data-unread={unread ? "1" : "0"}
            className={`w-full text-left flex gap-3 px-4 py-3 border-b border-[#eef0f3] transition-colors hover:bg-[#f5f6f8] ${unread ? "bg-[#f3f8fc]" : ""}`}>
      <Icon size={16} strokeWidth={1.7} className="mt-0.5 shrink-0 text-[#1c6a99]" />
      <span className="flex-1 min-w-0">
        <span className={`block text-[12.5px] text-[#0e1420] ${unread ? "font-semibold" : ""}`}>{n.message}</span>
        <span className="block text-[11px] text-[color:var(--cc-ink-muted)] mt-0.5">{fmtWhen(n.created_at)}</span>
      </span>
      {unread && <span className="mt-1.5 w-2 h-2 rounded-full bg-[#1c6a99] shrink-0" data-testid={`notif-unread-dot-${n.id}`} />}
    </button>
  );
}

function Panel({ items, unread, onOpen, onReadAll }) {
  return (
    <div className="absolute right-0 mt-2 w-[360px] max-w-[92vw] bg-white border border-[#e7e9ee] rounded-xl shadow-lg z-50 overflow-hidden"
         data-testid="notif-panel">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#e7e9ee]">
        <span className="text-[13px] font-semibold text-[#0e1420]">Notifications</span>
        <button type="button" onClick={onReadAll} disabled={!unread} data-testid="notif-mark-all-read"
                className="text-[11.5px] text-[#1c6a99] hover:underline disabled:opacity-40 disabled:no-underline">Mark all read</button>
      </div>
      <div className="max-h-[420px] overflow-y-auto">
        {items.length === 0
          ? <div className="px-4 py-8 text-center text-[12px] text-[color:var(--cc-ink-muted)]" data-testid="notif-empty">No notifications yet</div>
          : items.map((n) => <Row key={n.id} n={n} onOpen={onOpen} />)}
      </div>
    </div>
  );
}

export function NotificationCenterBell() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const refreshCount = useCallback(() => {
    api.get("/notification-center/unread-count").then(({ data }) => setUnread(data.unread_count)).catch(() => {});
  }, []);
  const loadList = useCallback(() => {
    api.get("/notification-center").then(({ data }) => { setItems(data.items); setUnread(data.unread_count); }).catch(() => {});
  }, []);

  useEffect(() => {
    refreshCount();
    const t = setInterval(refreshCount, POLL_MS);
    return () => clearInterval(t);
  }, [refreshCount]);
  useEffect(() => {
    if (!open) return undefined;
    loadList();
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open, loadList]);

  const onOpen = async (n) => {
    if (!n.read_at) {
      try {
        const { data } = await api.post(`/notification-center/${n.id}/read`, {});
        setItems((xs) => xs.map((x) => (x.id === n.id ? data.notification : x)));
        setUnread(data.unread_count);
      } catch { /* keep the row unread if the call fails */ }
    }
    if (n.link) { setOpen(false); nav(n.link); }
  };
  const onReadAll = async () => {
    try {
      await api.post("/notification-center/read-all", {});
      const now = new Date().toISOString();
      setItems((xs) => xs.map((x) => (x.read_at ? x : { ...x, read_at: now })));
      setUnread(0);
    } catch { /* badge stays as is */ }
  };

  const Icon = unread ? BellRing : Bell;
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-label="Notifications" data-testid="notif-bell-btn"
              className="relative p-2 rounded-lg hover:bg-[#f5f6f8] transition-colors">
        <Icon size={18} strokeWidth={1.7} className="text-[#0e1420]" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#d93b3b] text-white text-[10px] font-bold flex items-center justify-center"
                data-testid="notif-unread-badge">{unread > 99 ? "99+" : unread}</span>
        )}
      </button>
      {open && <Panel items={items} unread={unread} onOpen={onOpen} onReadAll={onReadAll} />}
    </div>
  );
}
